import { useState, useEffect, useRef, useCallback } from 'react';
import { useUserInteraction } from './useUserInteraction';

interface DataRefreshTimerConfig {
  refreshIntervalMs?: number;
  enabled?: boolean;
  pauseOnUserInteraction?: boolean; // Nova opção para pausar durante interação
  userIdleTimeoutMs?: number; // Tempo para considerar usuário inativo
  dependencies?: any[]; // Nova opção: dependências externas que reiniciam o timer
}

interface DataRefreshTimerReturn {
  isRefreshing: boolean;
  lastRefreshTime: Date | null;
  forceRefresh: () => void;
  setEnabled: (enabled: boolean) => void;
  isUserActive: boolean; // Novo: indica se usuário está ativo
  isPaused: boolean; // Novo: indica se timer está pausado por interação
}

export const useDataRefreshTimer = (
  refreshCallback: () => Promise<void> | void,
  config: DataRefreshTimerConfig = {}
): DataRefreshTimerReturn => {
  const {
    refreshIntervalMs = 15 * 1000, // 15 segundos por padrão
    enabled = true,
    pauseOnUserInteraction = true, // Pausar durante interação por padrão
    userIdleTimeoutMs = 3000, // 3 segundos para considerar usuário inativo
    dependencies = [] // Dependências externas que reiniciam o timer
  } = config;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [isEnabled, setIsEnabled] = useState(enabled);

  // Hook para detectar interação do usuário
  const { isUserActive } = useUserInteraction({
    idleTimeoutMs: userIdleTimeoutMs
  });

  // Calcular se o timer deve estar pausado
  const isPaused = pauseOnUserInteraction && isUserActive;

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshCallbackRef = useRef(refreshCallback);

  // Atualizar a referência do callback quando ele mudar
  useEffect(() => {
    refreshCallbackRef.current = refreshCallback;
  }, [refreshCallback]);

  const executeRefresh = useCallback(async () => {
    if (isRefreshing) return; // Evitar múltiplas execuções simultâneas

    // Adicionar pequeno delay para suavizar transições
    await new Promise(resolve => setTimeout(resolve, 100));
    
    setIsRefreshing(true);
    try {
      await refreshCallbackRef.current();
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  const startTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    // Só iniciar timer se estiver habilitado E não pausado por interação
    if (isEnabled && !isPaused) {
      refreshTimerRef.current = setInterval(() => {
        executeRefresh();
      }, refreshIntervalMs);
    }
  }, [isEnabled, isPaused, refreshIntervalMs, executeRefresh]);

  const stopTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const forceRefresh = useCallback(() => {
    executeRefresh();
  }, [executeRefresh]);

  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
  }, []);

  // Iniciar/parar timer baseado no estado enabled e pausado
  useEffect(() => {
    if (isEnabled && !isPaused) {
      startTimer();
    } else {
      stopTimer();
    }

    return () => {
      stopTimer();
    };
  }, [isEnabled, isPaused, startTimer, stopTimer, ...dependencies]);

  // Reiniciar timer quando dependências mudarem (ex: data selecionada)
  useEffect(() => {
    if (isEnabled && !isPaused) {
      // Força uma atualização imediata quando dependências mudam
      executeRefresh();
      // Reinicia o timer
      stopTimer();
      startTimer();
    }
  }, dependencies);

  // Cleanup ao desmontar o componente
  useEffect(() => {
    return () => {
      stopTimer();
    };
  }, [stopTimer]);

  return {
    isRefreshing,
    lastRefreshTime,
    forceRefresh,
    setEnabled,
    isUserActive,
    isPaused
  };
};